import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './SafeProductImage.module.scss';

const ProductPlaceholder = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path d="M10 15.5h28v25H10z" />
    <path d="M17 18v-4.5a7 7 0 0 1 14 0V18" />
    <path d="M10 22h28" />
  </svg>
);

/**
 * Product image with a deterministic, brand-neutral fallback.
 *
 * Catalog data often outlives a storefront image URL. Keeping this behavior in
 * one component prevents broken-image glyphs across selectors, merchandising,
 * catalog rows, and product details.
 */
const SafeProductImage = ({ src, alt, className, style, fallbackLabel }) => {
  const [loadedSource, setLoadedSource] = useState(null);
  const [failedSource, setFailedSource] = useState(null);
  const failed = !src || failedSource === src;
  const loaded = !failed && loadedSource === src;

  return (
    <span
      className={className}
      style={style}
      role={alt ? 'img' : undefined}
      aria-label={alt ? fallbackLabel || alt : undefined}
      aria-hidden={alt ? undefined : 'true'}
      data-image-state={failed ? 'fallback' : (loaded ? 'loaded' : 'loading')}
      data-testid={!loaded ? 'product-image-fallback' : undefined}
    >
      <span className={`${styles.fallback} ${loaded ? styles.loaded : ''}`} aria-hidden="true">
        {!loaded && <ProductPlaceholder />}
        {!failed && (
          <img
            src={src}
            alt=""
            className={styles.image}
            style={{
              display: loaded ? 'block' : 'none',
              objectFit: style?.objectFit || 'contain',
            }}
            onLoad={() => setLoadedSource(src)}
            onError={() => setFailedSource(src)}
          />
        )}
      </span>
    </span>
  );
};

SafeProductImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  fallbackLabel: PropTypes.string,
};

SafeProductImage.defaultProps = {
  src: '',
  alt: '',
  className: '',
  style: null,
  fallbackLabel: '',
};

export default SafeProductImage;
